import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Filter, RefreshCw, Calendar, Sparkles,
  Building2, CheckCircle2, Award, Zap, Phone, Mail, Clock
} from 'lucide-react';
import { bdAPI } from '../utils/api';

const BDHosts = () => {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('ALL');
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    fetchHosts();
  }, []);

  const fetchHosts = async () => {
    try {
      setLoading(true);
      const res = await bdAPI.getHosts();
      if (res.data?.success) {
        setHosts(res.data.hosts || []);
      }
    } catch (err) {
      console.error("Error fetching BD hosts:", err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique list of agencies from hosts
  const agencyOptions = Array.from(new Set(hosts.map(h => h.agency_name).filter(Boolean)));

  const filteredHosts = hosts.filter(h => {
    const matchesSearch = 
      h.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      h.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      h.xoxo_id?.toString().includes(searchTerm) ||
      h.agency_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAgency = agencyFilter === 'ALL' || h.agency_name === agencyFilter;

    return matchesSearch && matchesAgency;
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-wider mb-2">
            <Users size={16} />
            <span>BD Creator Network</span>
          </div>
          <h2 className="text-3xl font-black text-white tracking-tight">Agency Hosts</h2>
          <p className="text-slate-400 text-sm mt-1">
            Hosts and live streamers registered under your managed agencies portfolio.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchHosts()}
            className="p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-colors cursor-pointer"
            title="Refresh hosts"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <div className="px-4 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold">
            Total {hosts.length} Hosts
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search host name, XOXO ID, or agency..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#0f172a] border border-white/10 text-white pl-11 pr-4 py-3.5 rounded-2xl text-sm placeholder:text-slate-500 outline-none focus:border-cyan-500/50 transition-colors"
          />
        </div>

        <select
          value={agencyFilter}
          onChange={(e) => setAgencyFilter(e.target.value)}
          className="bg-[#0f172a] border border-white/10 text-white px-4 py-3.5 rounded-2xl text-sm outline-none focus:border-cyan-500"
        >
          <option value="ALL">All Managed Agencies</option>
          {agencyOptions.map(ag => (
            <option key={ag} value={ag}>{ag}</option>
          ))}
        </select>
      </div>

      {/* Hosts Table */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[32px] overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <div className="w-12 h-12 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-sm font-semibold">Loading agency hosts...</p>
          </div>
        ) : filteredHosts.length === 0 ? (
          <div className="p-16 text-center">
            <Users size={48} className="mx-auto mb-4 text-slate-700" />
            <h4 className="text-lg font-bold text-white">No Hosts Found</h4>
            <p className="text-slate-400 text-sm mt-1 max-w-sm mx-auto">
              {searchTerm || agencyFilter !== 'ALL' 
                ? 'No hosts match your selected filters.' 
                : 'Your managed agencies currently have no approved hosts.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="px-6 py-5">Host</th>
                  <th className="px-6 py-5">XOXO ID</th>
                  <th className="px-6 py-5">Managed Agency</th>
                  <th className="px-6 py-5">Target Level</th>
                  <th className="px-6 py-5">Valid Days</th>
                  <th className="px-6 py-5">Month Coins</th>
                  <th className="px-6 py-5">Joined Date</th>
                  <th className="px-6 py-5 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm">
                {filteredHosts.map((host) => (
                  <tr key={host.user_id || host.application_id} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-cyan-500/20 to-emerald-500/20 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-black text-sm">
                          {host.avatar_url ? (
                            <img src={host.avatar_url} alt="" className="w-full h-full rounded-2xl object-cover" />
                          ) : (
                            (host.first_name || 'H').charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-white group-hover:text-cyan-400 transition-colors">
                            {host.first_name} {host.last_name}
                          </div>
                          <div className="text-xs text-slate-400">
                            {host.country || 'India'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <span className="font-mono font-bold text-xs text-slate-300 bg-white/5 px-2.5 py-1 rounded-xl border border-white/10">
                        {host.xoxo_id || host.user_id?.substring(0, 8) || '---'}
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-emerald-400" />
                        <span className="font-semibold text-white">{host.agency_name}</span>
                        <span className="text-xs font-mono text-emerald-400">#{host.agency_code}</span>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                        Level {Number(host.current_level || 0)}
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-xl text-xs font-bold ${
                        Number(host.valid_days) >= 10 
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                          : 'bg-white/5 text-slate-300 border border-white/10'
                      }`}>
                        <Zap size={12} className={Number(host.valid_days) >= 10 ? 'text-emerald-400' : 'text-slate-500'} />
                        <span>{host.valid_days || 0} Days</span>
                      </span>
                    </td>

                    <td className="px-6 py-5 font-bold text-white">
                      {Number(host.monthly_target_coins || 0).toLocaleString()}
                    </td>

                    <td className="px-6 py-5 text-slate-400 text-xs">
                      {host.joined_at ? new Date(host.joined_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '---'}
                    </td>

                    <td className="px-6 py-5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          {host.host_status || 'Approved'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast Alert */}
      {toastMsg && (
        <div className="fixed bottom-8 right-8 z-50 bg-emerald-500 text-white px-5 py-3 rounded-2xl shadow-2xl text-xs font-bold flex items-center gap-2 animate-in slide-in-from-bottom-4">
          <CheckCircle2 size={16} />
          <span>{toastMsg}</span>
        </div>
      )}
    </div>
  );
};

export default BDHosts;
